import { useEffect, useState, useCallback } from 'react';
import {
  listSimulators,
  createSimulatorOnDaemon,
  deleteSimulatorOnDaemon,
  setSimulatorOnline,
  forceStartPort,
  forceStopPort,
  setAutoSessions,
  setAutoSessionsAll,
  createChargerOnBackend,
  deleteChargerOnBackend,
  type SimulatorSnapshot,
} from '../lib/api';
import AddChargerModal from './AddChargerModal';
import EditChargerModal from './EditChargerModal';
import BulkAddModal from './BulkAddModal';
import CsvBulkAddModal from './CsvBulkAddModal';
import ReattachModal from './ReattachModal';

const POLL_INTERVAL_MS = 5_000;

export default function Dashboard({ onLogout }: { onLogout: () => void }) {
  const [sims, setSims] = useState<SimulatorSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [showReattach, setShowReattach] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [autoAllBusy, setAutoAllBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listSimulators();
      setSims(data);
      setError(null);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) { onLogout(); return; }
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    refresh();
    // Poll every 5 s. A better world uses SSE, but polling is fine for
    // a low-traffic admin panel and needs zero server-side machinery.
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  async function withBusy(id: string, fn: () => Promise<unknown>) {
    setBusyId(id);
    try {
      await fn();
      await refresh();
    } catch (err: any) {
      alert(err?.response?.data?.error ?? err?.message ?? 'Action failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleAdd(payload: {
    charger_id: string;
    name: string;
    lat: number;
    lng: number;
    location: string;
    vendor_id?: string;
    org_id?: string;
    power_kw: number;
    is_public: boolean;
    ports: Array<{ port_number: number; connector_type: string }>;
  }) {
    // Two calls: create the DB row on the backend first, then hand
    // off the charger_id to the daemon so it opens a WS. If the backend
    // says the charger already exists (409), that's fine — proceed to
    // attach the existing row. This lets the same "Add" form recover
    // stranded chargers after a daemon redeploy.
    try {
      await createChargerOnBackend(payload);
    } catch (err: any) {
      if (err?.response?.status !== 409) throw err;
    }
    await createSimulatorOnDaemon(payload.charger_id, payload.ports.length);
    await refresh();
  }

  async function handleDelete(charger_id: string) {
    if (!confirm(`Delete ${charger_id}? This removes the charger from the DB and stops the simulator.`)) return;
    await withBusy(charger_id, async () => {
      await deleteSimulatorOnDaemon(charger_id);
      await deleteChargerOnBackend(charger_id);
    });
  }

  return (
    <div className="page">
      <div className="header">
        <div>
          <h1>Simulator Panel</h1>
          <div className="sub">
            {sims.length} simulator{sims.length === 1 ? '' : 's'} · daemon status live-polled every {POLL_INTERVAL_MS / 1000}s
          </div>
        </div>
        <button className="ghost" onClick={onLogout}>Sign out</button>
      </div>

      <div className="toolbar">
        <button onClick={() => setShowAdd(true)}>+ Add charger</button>
        <button onClick={() => setShowBulk(true)}>+ Bulk add</button>
        <button onClick={() => setShowCsv(true)}>+ CSV import</button>
        <button className="ghost" onClick={() => setShowReattach(true)}>Reattach existing</button>
        <button
          className="ghost"
          disabled={autoAllBusy || sims.length === 0}
          onClick={async () => {
            const anyOn = sims.some((s) => s.auto_sessions);
            const target = !anyOn;      // if any is on → turn all off; else turn all on
            setAutoAllBusy(true);
            try { await setAutoSessionsAll(target); await refresh(); }
            catch (err: any) { alert(err?.response?.data?.error ?? err?.message ?? 'Toggle failed'); }
            finally { setAutoAllBusy(false); }
          }}
        >
          {sims.some((s) => s.auto_sessions) ? 'Auto sessions: off (all)' : 'Auto sessions: on (all)'}
        </button>
        <button className="ghost" onClick={refresh}>Refresh</button>
      </div>

      {loading && <div className="muted">Loading…</div>}
      {error && <div className="error">{error}</div>}

      {!loading && sims.length === 0 && (
        <div className="card">
          <div className="muted">No simulators yet. Click "+ Add charger" to create one, or run the seed script for the initial 10.</div>
        </div>
      )}

      {sims.map((s) => (
        <SimulatorCard
          key={s.charger_id}
          sim={s}
          busy={busyId === s.charger_id}
          onToggleOnline={(online) => withBusy(s.charger_id, () => setSimulatorOnline(s.charger_id, online))}
          onStartPort={(port) => withBusy(s.charger_id, () => forceStartPort(s.charger_id, port))}
          onStopPort={(port) => withBusy(s.charger_id, () => forceStopPort(s.charger_id, port))}
          onToggleAuto={(enabled) => withBusy(s.charger_id, () => setAutoSessions(s.charger_id, enabled))}
          onEdit={() => setEditId(s.charger_id)}
          onDelete={() => handleDelete(s.charger_id)}
        />
      ))}

      {showAdd && (
        <AddChargerModal
          onClose={() => setShowAdd(false)}
          onSubmit={handleAdd}
        />
      )}
      {showBulk && (
        <BulkAddModal
          existing={sims}
          onClose={() => setShowBulk(false)}
          onDone={refresh}
        />
      )}
      {showCsv && (
        <CsvBulkAddModal
          onClose={() => setShowCsv(false)}
          onDone={refresh}
        />
      )}
      {editId && (
        <EditChargerModal
          charger_id={editId}
          onClose={() => setEditId(null)}
          onSaved={refresh}
        />
      )}
      {showReattach && (
        <ReattachModal
          onClose={() => setShowReattach(false)}
          onDone={refresh}
        />
      )}
    </div>
  );
}

interface CardProps {
  sim: SimulatorSnapshot;
  busy: boolean;
  onToggleOnline: (online: boolean) => void;
  onStartPort: (port: number) => void;
  onStopPort: (port: number) => void;
  onToggleAuto: (enabled: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}

function SimulatorCard({ sim, busy, onToggleOnline, onStartPort, onStopPort, onToggleAuto, onEdit, onDelete }: CardProps) {
  const online = sim.connected && !sim.forced_offline && !sim.in_outage;
  return (
    <div className="card">
      <div className="row between">
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{sim.charger_id}</div>
          <div className="muted">
            {sim.port_count} port{sim.port_count === 1 ? '' : 's'} · draw {sim.current_ma} mA
            {sim.ws_state && <span style={{ marginLeft: 6 }}>· ws {sim.ws_state}</span>}
            {sim.auto_sessions && <span style={{ marginLeft: 6, color: 'var(--success)' }}>· auto ●</span>}
          </div>
          {sim.last_error && (
            <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>
              Last error: {sim.last_error}
              {typeof sim.consecutive_failures === 'number' && sim.consecutive_failures > 3 && (
                <span> · {sim.consecutive_failures} attempts failed in a row — backend proxy may be wedged, try restarting csms.magmatech.in</span>
              )}
            </div>
          )}
        </div>
        <StatusPill sim={sim} online={online} />
      </div>

      <div className="port-grid">
        {sim.ports.map((p) => (
          <div key={p.port} className="port">
            <div className="port-head">
              <span className="port-name">Port {p.port}</span>
              <span className={`pill ${p.is_charging ? 'online' : 'offline'}`}>
                {p.is_charging ? 'Charging' : p.is_enabled ? 'Idle' : 'Disabled'}
              </span>
            </div>
            {p.session_id && <div className="muted">Session: {p.session_id.slice(0, 16)}…</div>}
            <div className="port-buttons">
              <button
                className="success"
                onClick={() => onStartPort(p.port)}
                disabled={busy || !p.is_enabled || p.is_charging}
              >Start</button>
              <button
                className="warn"
                onClick={() => onStopPort(p.port)}
                disabled={busy || !p.is_charging}
              >Stop</button>
            </div>
          </div>
        ))}
      </div>

      <div className="row mt" style={{ gap: 8 }}>
        <button
          className={sim.forced_offline ? 'success' : 'warn'}
          onClick={() => onToggleOnline(sim.forced_offline)}
          disabled={busy}
        >
          {sim.forced_offline ? 'Bring online' : 'Force offline'}
        </button>
        <button
          className={sim.auto_sessions ? 'warn' : 'ghost'}
          onClick={() => onToggleAuto(!sim.auto_sessions)}
          disabled={busy}
          title="Randomly start / stop fake sessions on idle ports every ~30s"
        >
          {sim.auto_sessions ? 'Auto: on' : 'Auto: off'}
        </button>
        <button className="ghost" onClick={onEdit} disabled={busy}>Edit</button>
        <button className="danger" onClick={onDelete} disabled={busy}>Delete</button>
      </div>
    </div>
  );
}

function StatusPill({ sim, online }: { sim: SimulatorSnapshot; online: boolean }) {
  if (sim.in_outage) {
    return <span className="pill outage"><span className="dot" />Micro-outage</span>;
  }
  if (sim.forced_offline) {
    return <span className="pill offline"><span className="dot" />Forced offline</span>;
  }
  return (
    <span className={`pill ${online ? 'online' : 'offline'}`}>
      <span className="dot" />
      {online ? 'Online' : 'Reconnecting…'}
    </span>
  );
}
